import React, { useState, useEffect } from 'react';
import Login from './components/Login';
import OverviewDashboard from './components/OverviewDashboard';
import ScriptTemplates from './components/ScriptTemplates';
import VoiceAgents from './components/VoiceAgents';
import CSVCampaigns from './components/CSVCampaigns';
import ReportsHistory from './components/ReportsHistory';
import UsersManager from './components/UsersManager';
import SetupGuide from './components/SetupGuide';
import TokenConsumption from './components/TokenConsumption';

import {
  TwilioScript,
  Agent,
  Campaign,
  ConversationSummary,
  fetchExotelScripts,
  fetchExotelAgents,
  fetchCampaigns,
  fetchExotelConversations,
} from './utils/api';
import './App.css';

export type DashboardView = 'dashboard' | 'scripts' | 'agents' | 'campaigns' | 'conversations' | 'users' | 'setup' | 'consumption';

interface LoggedInUser {
  id: number;
  username: string;
  role: 'super_admin' | 'admin' | 'customer';
}

const hashToView: Record<string, DashboardView> = {
  '#/dashboard': 'dashboard',
  '#/scripts': 'scripts',
  '#/agents': 'agents',
  '#/campaigns': 'campaigns',
  '#/conversations': 'conversations',
  '#/users': 'users',
  '#/setup': 'setup',
  '#/consumption': 'consumption',
};

const viewToHash: Record<DashboardView, string> = {
  dashboard: '#/dashboard',
  scripts: '#/scripts',
  agents: '#/agents',
  campaigns: '#/campaigns',
  conversations: '#/conversations',
  users: '#/users',
  setup: '#/setup',
  consumption: '#/consumption',
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<LoggedInUser | null>(null);
  const [view, setView] = useState<DashboardView>('dashboard');
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Global counts & arrays for metrics mapping
  const [scripts, setScripts] = useState<TwilioScript[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);

  const loadGlobalMetrics = async () => {
    try {
      const [sList, aList, cList, rList] = await Promise.all([
        fetchExotelScripts().catch(() => []),
        fetchExotelAgents().catch(() => []),
        fetchCampaigns().catch(() => []),
        fetchExotelConversations().catch(() => []),
      ]);
      setScripts(sList);
      setAgents(aList);
      setCampaigns(cList);
      setConversations(rList);
    } catch (err) {
      console.error('Failed to load global metrics:', err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        const u = JSON.parse(savedUser) as LoggedInUser;
        setUser(u);
        setIsAuthenticated(true);
        // Set initial hash
        const initialHash = window.location.hash;
        if (initialHash && hashToView[initialHash]) {
          if (u.role === 'customer' && hashToView[initialHash] !== 'conversations') {
            window.location.hash = '#/conversations';
          }
        } else {
          window.location.hash = u.role === 'customer' ? '#/conversations' : '#/dashboard';
        }
      } catch {
        localStorage.clear();
      }
    }
  }, []);

  // Hash change synchronization listener
  useEffect(() => {
    const handleHashChange = () => {
      const currentHash = window.location.hash || '#/dashboard';
      const matchedView = hashToView[currentHash] || 'dashboard';

      if (isAuthenticated && user) {
        if (user.role === 'customer' && matchedView !== 'conversations') {
          window.location.hash = '#/conversations';
          setView('conversations');
        } else {
          setView(matchedView);
        }
      }
    };

    if (isAuthenticated && user) {
      handleHashChange();
      window.addEventListener('hashchange', handleHashChange);
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [isAuthenticated, user]);

  // Fetch or refresh metrics whenever user is authenticated or switches view
  useEffect(() => {
    if (isAuthenticated && user && user.role !== 'customer') {
      loadGlobalMetrics();
    }
  }, [isAuthenticated, user, view]);

  const handleLoginSuccess = (token: string, loggedInUser: LoggedInUser) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(loggedInUser));
    setUser(loggedInUser);
    setIsAuthenticated(true);

    window.location.hash = loggedInUser.role === 'customer' ? '#/conversations' : '#/dashboard';
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setShowProfileMenu(false);
    window.location.hash = '';
  };

  useEffect(() => {
    const handleUnauthorized = () => {
      console.warn('[App] Session expired/unauthorized. Logging out...');
      handleLogout();
    };
    window.addEventListener('unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('unauthorized', handleUnauthorized);
    };
  }, []);

  const handleViewChange = (newView: DashboardView) => {
    window.location.hash = viewToHash[newView];
  };

  if (!isAuthenticated || !user) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Map active view to header title
  const viewTitles: Record<DashboardView, string> = {
    dashboard: 'Dashboard',
    scripts: 'Script Templates',
    agents: 'Voice Agents',
    campaigns: 'CSV Call Campaigns',
    conversations: 'Reports & History',
    users: 'Users & Security',
    setup: 'Setup Guide',
    consumption: 'Token Consumption',
  };

  return (
    <div className="admin-shell">
      {/* ─── Left Sidebar Navigation ─── */}
      <aside className="sidebar-nav">
        <div className="sidebar-brand">
          <svg className="brand-logo-svg" viewBox="0 0 24 24" width="30" height="30">
            <rect x="3" y="12" width="4" height="8" rx="1.5" fill="#a855f7" />
            <rect x="10" y="7" width="4" height="13" rx="1.5" fill="#3b82f6" />
            <rect x="17" y="3" width="4" height="17" rx="1.5" fill="#10b981" />
          </svg>
          <span className="brand-name">
            <span className="purple-i">i</span>FLOW
          </span>
        </div>

        <div className="nav-section">
          {user.role !== 'customer' && (
            <button
              className={`nav-item-btn${view === 'dashboard' ? ' active' : ''}`}
              onClick={() => handleViewChange('dashboard')}
            >
              <span className="nav-icon">📊</span>
              <span>Dashboard</span>
            </button>
          )}
        </div>

        <div className="nav-section-label">MODULES</div>
        
        <div className="nav-section">
          {user.role !== 'customer' && (
            <>
              <button
                className={`nav-item-btn${view === 'scripts' ? ' active' : ''}`}
                onClick={() => handleViewChange('scripts')}
              >
                <span className="nav-icon">📝</span>
                <span>Scripts Template</span>
              </button>
              
              <button
                className={`nav-item-btn${view === 'agents' ? ' active' : ''}`}
                onClick={() => handleViewChange('agents')}
              >
                <span className="nav-icon">🤖</span>
                <span>Voice Agents</span>
              </button>
              
              <button
                className={`nav-item-btn${view === 'campaigns' ? ' active' : ''}`}
                onClick={() => handleViewChange('campaigns')}
              >
                <span className="nav-icon">🚀</span>
                <span>CSV Campaigns</span>
              </button>
            </>
          )}

          <button
            className={`nav-item-btn${view === 'conversations' ? ' active' : ''}`}
            onClick={() => handleViewChange('conversations')}
          >
            <span className="nav-icon">📋</span>
            <span>Reports & History</span>
          </button>

          {user.role !== 'customer' && (
            <>
              <button
                className={`nav-item-btn${view === 'users' ? ' active' : ''}`}
                onClick={() => handleViewChange('users')}
              >
                <span className="nav-icon">👥</span>
                <span>Users Manager</span>
              </button>

              <button
                className={`nav-item-btn${view === 'consumption' ? ' active' : ''}`}
                onClick={() => handleViewChange('consumption')}
              >
                <span className="nav-icon">🪙</span>
                <span>Token Consumption</span>
              </button>
              
              <button
                className={`nav-item-btn${view === 'setup' ? ' active' : ''}`}
                onClick={() => handleViewChange('setup')}
              >
                <span className="nav-icon">⚙️</span>
                <span>Setup Guide</span>
              </button>
            </>
          )}
        </div>
      </aside>

      {/* ─── Right Main Panel ─── */}
      <div className="main-panel">
        
        {/* Top Header Bar */}
        <header className="panel-header">
          <div className="header-title-container">
            {view === 'dashboard' && <span className="header-icon-chart">📊</span>}
            <h2 className="panel-title">{viewTitles[view]}</h2>
          </div>
          
          <div className="user-profile-menu">
            <button 
              className="profile-trigger-btn"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              <div className="avatar-circle">
                {user.username.charAt(0).toUpperCase()}
              </div>
              <span className="username-text">{user.username}</span>
              <span className="role-text">
                {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'Customer'}
              </span>
              <span className="dropdown-arrow">▼</span>
            </button>

            {showProfileMenu && (
              <div className="profile-dropdown-card">
                <div className="dropdown-user-details">
                  <strong>{user.username}</strong>
                  <span>{user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'Customer'} Account</span>
                </div>
                <hr className="dropdown-divider" />
                <button className="dropdown-logout-btn" onClick={handleLogout}>
                  🚪 Sign Out
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Dynamic Main Body Content */}
        <main className="panel-body">
          {view === 'dashboard' && user.role !== 'customer' && (
            <OverviewDashboard
              userRole={user.role}
              scripts={scripts}
              agents={agents}
              campaigns={campaigns}
              conversations={conversations}
              setView={handleViewChange}
            />
          )}

          {view === 'scripts' && user.role !== 'customer' && (
            <ScriptTemplates userRole={user.role} />
          )}

          {view === 'agents' && user.role !== 'customer' && (
            <VoiceAgents userRole={user.role} />
          )}

          {view === 'campaigns' && user.role !== 'customer' && (
            <CSVCampaigns userRole={user.role} />
          )}

          {view === 'conversations' && (
            <ReportsHistory userRole={user.role} />
          )}

          {view === 'users' && user.role !== 'customer' && (
            <UsersManager userRole={user.role} />
          )}

          {view === 'consumption' && user.role !== 'customer' && (
            <TokenConsumption userRole={user.role} />
          )}

          {view === 'setup' && user.role !== 'customer' && (
            <SetupGuide userRole={user.role} />
          )}
        </main>

      </div>
    </div>
  );
};

export default App;
